import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {FaBook, FaHistory, FaStar} from 'react-icons/fa';
import {useSongLibrary} from './SongLibraryContext';
import {useSelectSong} from '../../navigation/components/Navigation';
import {normalizeSearchText} from '../services/song-search-text';
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';
import './SongLibraryPages.css';

const views = {
  catalog: {title: 'Katalog utworów', icon: FaBook, url: '/app/song-library', empty: 'Brak utworów w tej sesji.'},
  favorites: {title: 'Ulubione utwory', icon: FaStar, url: '/app/song-favorites', empty: 'Dodaj utwór do ulubionych przyciskiem gwiazdki.'},
  history: {title: 'Historia użycia', icon: FaHistory, url: '/app/song-history', empty: 'Otwórz utwór, aby pojawił się w historii.'}
};
const entriesFor = (entries, kind) => kind === 'catalog' ? entries.filter(entry => entry.variants.length)
  : kind === 'favorites' ? entries.filter(entry => entry.favorite)
  : entries.filter(entry => entry.count).sort((a, b) => b.lastAt.localeCompare(a.lastAt));
const plural = (count, one, few, many) => `${count} ${count === 1 ? one : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? few : many}`;
const songCount = count => plural(count, 'utwór', 'utwory', 'utworów');
const dateLabel = date => new Date(date).toLocaleString('pl-PL');

export const FavoriteButton = ({entry}) => {
  const {toggleFavorite} = useSongLibrary();
  if (!entry) return null;
  return <button type="button" className={'song-favorite-button' + (entry.favorite ? ' is-favorite' : '')}
    aria-pressed={entry.favorite} title={entry.favorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
    aria-label={`${entry.favorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}: ${entry.title}`}
    onClick={event => { event.stopPropagation(); toggleFavorite(entry); }}><FaStar aria-hidden="true"/></button>;
};

export const SongLibraryTiles = () => {
  const {entries, loading, error} = useSongLibrary();
  const selectSong = useSelectSong();
  return <section className="song-library-tiles" aria-label="Utwory, ulubione i historia">
    {Object.entries(views).map(([kind, view]) => {
      const items = entriesFor(entries, kind);
      const Icon = view.icon;
      return <article key={kind} className="dashboard-tile song-library-tile">
        <div className="dashboard-tile-heading"><Icon aria-hidden="true"/><h2><Link to={view.url}>{view.title}</Link></h2></div>
        <p className="dashboard-tile-subtitle">{loading ? 'Ładowanie…' : songCount(items.length)}</p>
        <div className="song-library-tile-items">{items.slice(0, 3).map(entry => <button type="button" key={entry.id}
          disabled={!entry.variants.length} onClick={() => selectSong(entry.variants[0].fileName, entry.variants[0].songNumber)}>
          {entry.title}{!entry.variants.length && ' · brak źródła'}
        </button>)}</div>
        <Link className="dashboard-more" to={view.url} aria-label={`Pełna lista: ${view.title}`}>Pełna lista →</Link>
      </article>;
    })}
    {error && <p role="alert">Nie udało się wczytać katalogu: {error}</p>}
  </section>;
};

const SongLibraryRow = ({entry}) => {
  const [expanded, setExpanded] = useState(false);
  const selectSong = useSelectSong();
  const first = entry.variants[0];
  return <article className="song-library-row" aria-label={`Utwór: ${entry.title}`}>
    <div className="song-library-row-head">
      <FavoriteButton entry={entry}/>
      <button type="button" className="song-library-open" disabled={!first} onClick={() => selectSong(first.fileName, first.songNumber)}>{entry.title}</button>
      <button type="button" className="song-library-versions" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {plural(entry.variants.length, 'lokalizacja', 'lokalizacje', 'lokalizacji')} {expanded ? '▴' : '▾'}
      </button>
    </div>
    <div className="song-library-activity">{entry.count > 0 ? <><span>{plural(entry.count, 'otwarcie', 'otwarcia', 'otwarć')} · {plural(entry.days, 'dzień', 'dni', 'dni')}</span>
      <time dateTime={entry.lastAt}>Ostatnio: {dateLabel(entry.lastAt)}</time></> : <span>Jeszcze nie otwierano</span>}</div>
    {!first && <p className="dashboard-empty">Brak źródła — utwór usunięto z bieżących śpiewników.</p>}
    {expanded && <div className="song-library-details">
      {entry.variants.map(variant => <section key={variant.id} className="song-library-variant">
        <button type="button" onClick={() => selectSong(variant.fileName, variant.songNumber)}>{variant.fileName} · pieśń {variant.songNumber}</button>
        <pre>{variant.lyrics}</pre>
      </section>)}
      {entry.lastSource && <p className="dashboard-empty">Ostatnia lokalizacja użycia: {entry.lastSource.fileName} · pieśń {entry.lastSource.songNumber}</p>}
    </div>}
  </article>;
};

export const SongLibraryPage = ({kind = 'catalog'}) => {
  const isReadOnly = useWorkspaceReadOnly();
  const {entries, loading, error} = useSongLibrary();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const view = views[kind];
  const Icon = view.icon;
  const items = useMemo(() => entriesFor(entries, kind).filter(entry => normalizeSearchText(
    [entry.title, entry.lyrics, ...entry.variants.flatMap(variant => [variant.fileName, variant.lyrics])].join(' ')
  ).includes(normalizeSearchText(query))), [entries, kind, query]);
  useEffect(() => setPage(0), [kind, query]);
  const pageCount = Math.max(1, Math.ceil(items.length / 40));
  const currentPage = Math.min(page, pageCount - 1);
  return <main className="dashboard-page song-library-page">
    <nav className="song-library-nav" aria-label="Nawigacja utworów"><Link to="/app/dashboard">Strona główna</Link>
      {Object.entries(views).map(([name, item]) => <Link key={name} to={item.url} aria-current={kind === name ? 'page' : undefined}>{item.title}</Link>)}
    </nav>
    <div className="dashboard-page-head"><h1><Icon aria-hidden="true"/>{view.title}</h1><span>{songCount(items.length)}</span></div>
    <p className="dashboard-empty">{kind === 'history' ? 'Historia otwarć utworów, łączona przez wszystkie zestawy i wersje. Otwarcie śpiewnika liczy pierwszą pieśń; wybór konkretnej pieśni liczy tę pieśń.'
      : 'Jeden wpis zbiera kopie i podobne wersje utworu. Rozwiń lokalizacje, aby wybrać konkretną wersję.'}</p>
    <p className="dashboard-empty">{(isReadOnly || process.env.REACT_APP_HOSANNA_DIRECT === '1') ? 'Ulubione i historia są osobiste, zapisane w tej przeglądarce.' : 'Ulubione i historia są wspólne dla tej sesji i synchronizowane między urządzeniami.'}</p>
    <label className="song-library-filter">Filtruj utwory<input type="search" autoComplete="off" value={query}
      onChange={event => setQuery(event.target.value)} placeholder="Tytuł, tekst lub śpiewnik"/></label>
    {loading && <p role="status">Ładowanie katalogu…</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && !items.length && <p className="dashboard-empty">{query ? 'Brak pasujących utworów.' : view.empty}</p>}
    <div className="song-library-list">{items.slice(currentPage * 40, (currentPage + 1) * 40).map(entry => <SongLibraryRow key={entry.id} entry={entry}/>)}</div>
    {pageCount > 1 && <nav className="song-library-pagination" aria-label="Strony katalogu">
      <button disabled={!currentPage} onClick={() => setPage(currentPage - 1)}>Poprzednia</button>
      <span>{currentPage + 1} / {pageCount}</span>
      <button disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)}>Następna</button>
    </nav>}
  </main>;
};
